import { useState } from 'react';
import { Table, TableBody } from '@/components/ui';
import { DataTablePagination, PanelInfo } from '@/components/custom';
import { useTransactions } from '../../hooks';
import type{ ITransaction } from '@/interfaces';
import { DataTablePageSizeEnum } from '@/enums';
import { Eye } from 'lucide-react';
import { useUserConnected } from '@/hooks';
import DataTableHeader from './DataTableHeader';
import DataTableBodySkeleton from './DataTableBodySkeleton';
import DataTableMobileSkeleton from './DataTableMobileSkeleton';
import {
    TransactionDetailsModal,
    TransactionDeleteModal,
    DataTableDesktopRow,
    DataTableMobileCard
} from './components';

export const DataTable = () => {
    const { data, isLoading, transactions, deleteTransaction } = useTransactions();
    const { userConnected } = useUserConnected();
    const isRoot = !!userConnected?.root;
    const currentUserId = userConnected?._id;
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<ITransaction | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [transactionToDelete, setTransactionToDelete] = useState<ITransaction | null>(null);

    const canDeleteTransaction = (transaction: ITransaction) =>
        isRoot || (!!currentUserId && transaction.createdByUserId === currentUserId);

    const handleViewDetails = (transaction: ITransaction) => {
        setSelectedTransaction(transaction);
        setIsDetailsModalOpen(true);
    };

    const handleDeleteClick = (transaction: ITransaction) => {
        if (!canDeleteTransaction(transaction)) return;
        setTransactionToDelete(transaction);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (!transactionToDelete) {
            return;
        }

        const id = transactionToDelete._id;
        const isOwner = !!currentUserId && transactionToDelete.createdByUserId === currentUserId;
        setIsDeleteModalOpen(false);
        deleteTransaction(id, isOwner);
    };

    if (isLoading) {
        return (
            <div className="space-y-5">
                <div className="hidden md:block border rounded-md">
                    <Table>
                        <DataTableHeader />
                        <DataTableBodySkeleton rows={DataTablePageSizeEnum._10} />
                    </Table>
                </div>
                <div className="block md:hidden">
                    <DataTableMobileSkeleton count={DataTablePageSizeEnum._10} />
                </div>
            </div>
        );
    }

    if (transactions.length === 0) {
        return (
            <PanelInfo
                title='Aucune transaction trouvée'
                description={`Vous ne disposez d'aucune transaction correspondant à ces critères.`}
                className='py-10 md:py-26 border-none'
                icon={Eye} />
        );
    }

    return (
        <div className="space-y-5">
            <div className="hidden md:block border rounded-md">
                <Table>
                    <DataTableHeader />
                    <TableBody>
                        {transactions.map((transaction) => (
                            <DataTableDesktopRow
                                key={transaction._id}
                                transaction={transaction}
                                onViewDetails={handleViewDetails}
                                onDelete={handleDeleteClick}
                                canDelete={canDeleteTransaction(transaction)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className="block md:hidden">
                <div className="space-y-4">
                    {transactions.map((transaction) => (
                        <DataTableMobileCard
                            key={transaction._id}
                            transaction={transaction}
                            onViewDetails={handleViewDetails}
                            onDelete={handleDeleteClick}
                            canDelete={canDeleteTransaction(transaction)}
                        />
                    ))}
                </div>
            </div>

            {data?.pagination && <DataTablePagination pageCount={data.pagination.pageCount} />}

            <TransactionDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                transaction={selectedTransaction}
            />

            <TransactionDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                transaction={transactionToDelete}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};

export default DataTable;
